import { type FC, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import type { SalesAttributionOverlapSet } from "../utils/salesAttributionTypes";
import { SOURCE_COLORS } from "./salesAttributionShared";

const VIEWBOX_WIDTH = 310;
const VIEWBOX_HEIGHT = 204;
const CENTER_Y = 100;
const RADIUS = 78;

interface OverlapVennChartProps {
  title: string;
  stats: SalesAttributionOverlapSet;
}

type HoverKey = "google" | "meta" | "both" | null;


export const OverlapVennChart: FC<OverlapVennChartProps> = ({ title, stats }) => {
  const { t } = useTranslation();
  const titleId = useId();
  const [hover, setHover] = useState<HoverKey>(null);

  const GOOGLE_LABEL = t("salesAttribution.ui.venn.googleLabel", { defaultValue: "Google Ads" });
  const META_LABEL = t("salesAttribution.ui.venn.metaLabel", { defaultValue: "Meta Ads" });

  const share = Math.min(1, Math.max(0, stats.overlapPercent.value / 100));

  const MAX_SEPARATION = RADIUS * 2 - 6;
  const MIN_SEPARATION = RADIUS * 0.36;
  const separation = MAX_SEPARATION - share * (MAX_SEPARATION - MIN_SEPARATION);
  const cx = VIEWBOX_WIDTH / 2 - separation / 2;
  const cx2 = VIEWBOX_WIDTH / 2 + separation / 2;

  const googleTooltip = t("salesAttribution.ui.venn.onlyClaimedTooltip", {
    defaultValue: "{{value}} claimed only by {{label}}",
    value: stats.googleOnly.formattedValue,
    label: GOOGLE_LABEL,
  });
  const metaTooltip = t("salesAttribution.ui.venn.onlyClaimedTooltip", {
    defaultValue: "{{value}} claimed only by {{label}}",
    value: stats.metaOnly.formattedValue,
    label: META_LABEL,
  });
  const bothTooltip = t("salesAttribution.ui.venn.bothTooltip", {
    defaultValue: "{{value}} ({{percent}}) claimed by both {{google}} and {{meta}}",
    value: stats.claimedByBoth.formattedValue,
    percent: stats.overlapPercent.formattedValue,
    google: GOOGLE_LABEL,
    meta: META_LABEL,
  });

  return (
    <div className="sales-attribution__venn">
      <p className="sales-attribution__venn-title" id={titleId}>
        {title}
      </p>
      <svg
        className="sales-attribution__venn-svg"
        viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
        role="img"
        aria-labelledby={titleId}
      >
        <circle
          className="sales-attribution__venn-shape"
          cx={cx}
          cy={CENTER_Y}
          r={RADIUS}
          fill={SOURCE_COLORS.googleAds}
          fillOpacity={hover === "google" ? 0.6 : 0.4}
          stroke={SOURCE_COLORS.googleAds}
          strokeWidth={2}
          onMouseEnter={() => setHover("google")}
          onMouseLeave={() => setHover(null)}
        />
        <circle
          className="sales-attribution__venn-shape"
          cx={cx2}
          cy={CENTER_Y}
          r={RADIUS}
          fill={SOURCE_COLORS.metaAds}
          fillOpacity={hover === "meta" ? 0.6 : 0.4}
          stroke={SOURCE_COLORS.metaAds}
          strokeWidth={2}
          onMouseEnter={() => setHover("meta")}
          onMouseLeave={() => setHover(null)}
        />
        <ellipse
          cx={VIEWBOX_WIDTH / 2}
          cy={CENTER_Y}
          rx={Math.max(6, (cx + RADIUS - (cx2 - RADIUS)) / 2)}
          ry={70}
          fill="transparent"
          onMouseEnter={() => setHover("both")}
          onMouseLeave={() => setHover(null)}
        />

        <text
          className="sales-attribution__venn-value"
          x={cx - RADIUS * 0.42}
          y={CENTER_Y + 4}
          textAnchor="middle"
        >
          {stats.googleOnly.formattedValue}
        </text>
        <text
          className="sales-attribution__venn-value sales-attribution__venn-value--overlap"
          x={VIEWBOX_WIDTH / 2}
          y={CENTER_Y + 4}
          textAnchor="middle"
        >
          {stats.claimedByBoth.formattedValue}
        </text>
        <text
          className="sales-attribution__venn-value"
          x={cx2 + RADIUS * 0.42}
          y={CENTER_Y + 4}
          textAnchor="middle"
        >
          {stats.metaOnly.formattedValue}
        </text>

        <text
          className="sales-attribution__venn-share"
          x={VIEWBOX_WIDTH / 2}
          y={VIEWBOX_HEIGHT - 14}
          textAnchor="middle"
        >
          {stats.overlapPercent.formattedValue}{" "}
          {t("salesAttribution.ui.venn.overlapSuffix", { defaultValue: "overlap" })}
        </text>
      </svg>

      {hover ? (
        <div className="sales-attribution__venn-tooltip">
          <span className="sales-attribution__venn-tooltip-label">
            {hover === "google"
              ? GOOGLE_LABEL
              : hover === "meta"
                ? META_LABEL
                : t("salesAttribution.ui.venn.bothPlatforms", { defaultValue: "Both platforms" })}
          </span>
          <span className="sales-attribution__venn-tooltip-detail">
            {hover === "google" ? googleTooltip : hover === "meta" ? metaTooltip : bothTooltip}
          </span>
        </div>
      ) : null}

      <div className="sales-attribution__venn-legend">
        <span
          className="sales-attribution__venn-legend-item"
          onMouseEnter={() => setHover("google")}
          onMouseLeave={() => setHover(null)}
        >
          <span
            className="sales-attribution__venn-legend-dot"
            style={{ background: SOURCE_COLORS.googleAds }}
          />
          {t("salesAttribution.ui.venn.claims", {
            defaultValue: "{{label}} claims ({{value}})",
            label: GOOGLE_LABEL,
            value: stats.googleClaims.formattedValue,
          })}
        </span>
        <span
          className="sales-attribution__venn-legend-item"
          onMouseEnter={() => setHover("meta")}
          onMouseLeave={() => setHover(null)}
        >
          <span
            className="sales-attribution__venn-legend-dot"
            style={{ background: SOURCE_COLORS.metaAds }}
          />
          {t("salesAttribution.ui.venn.claims", {
            defaultValue: "{{label}} claims ({{value}})",
            label: META_LABEL,
            value: stats.metaClaims.formattedValue,
          })}
        </span>
        <span
          className="sales-attribution__venn-legend-item"
          onMouseEnter={() => setHover("both")}
          onMouseLeave={() => setHover(null)}
        >
          {t("salesAttribution.ui.venn.middleBoth", { defaultValue: "Middle = claimed by both" })}
        </span>
      </div>
    </div>
  );
};

export default OverlapVennChart;
